import { ComponentPropsWithoutRef, ElementType, MouseEventHandler } from "react"
import { DEFAULT_ELEMENT } from "./constants"

/**
 * This function returns the standard attributes of button components. Use when you need to make a custom button
 */
export const getButtonBoxAttrs = <E extends ElementType = typeof DEFAULT_ELEMENT>({
  as: element = DEFAULT_ELEMENT,
  disabled: disabledProp,
  href = undefined,
  isLoading = false,
  onClick: onClickProp,
  ...rest
}: {
  as?: ElementType
  disabled?: boolean
  isLoading?: boolean
  onClick?: ComponentPropsWithoutRef<E>["onClick"]
  href?: string
} & Record<string | number, unknown>) => {
  const loadingAttrs = isLoading ? { "aria-disabled": true } : undefined

  const isAnchorElement = element === "a"

  let attrs: ComponentPropsWithoutRef<"button" | "a"> = {
    disabled: disabledProp,
  }

  if (isAnchorElement) {
    attrs = disabledProp
      ? { role: "link" as const, "aria-disabled": true }
      : {
          href,
        }
  }

  const onClick: MouseEventHandler<E> = e => {
    if (isLoading) {
      return
    }
    onClickProp?.(e)
  }

  return {
    ...rest,
    isDisabledStyling: Boolean(disabledProp || rest["aria-disabled"]),
    as: element,
    onClick,
    ...attrs,
    ...loadingAttrs,
  }
}
